// Tela de Integração ERP — importação de clientes vindos do ERP.
//
// A listagem é paginada no servidor. Antes a tela baixava as 220 mil linhas de
// uma vez e filtrava, ordenava e recortava no navegador; agora cada uma dessas
// três coisas acontece no Postgres e o que chega é só a página à vista.
//
// Isso muda duas coisas no comportamento da tela:
//
//   - Todo filtro vira uma nova requisição. A busca livre é debounced para não
//     disparar uma por tecla.
//   - A seleção guarda o `id` da linha de clientesErp, não a linha inteira, porque as linhas
//     das outras páginas não estão mais em memória. Para importar mais do que
//     cabe numa página existe o modo "todos os resultados", que manda o filtro
//     ao servidor em vez da lista de códigos.
import { useTheme } from "@emotion/react";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Alert,
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FilterBar from "../../../../components/topBarFilter";
import TabelaClientesErp from "./dataTable";
import ProgressoImportacao from "./componentes/progressoImportacao";
import {
  criarFormatadorMascara,
  renderChips,
  renderTexto,
  renderTipoCliente,
  renderErp,
} from "./dataTable/renderers";
import useStyles from "./styles";

import { Clear, Search } from "@mui/icons-material";
import { Box } from "@mui/system";
import { useOutletContext } from "react-router-dom";
import {
  maskCellPhone,
  maskCnpj,
  maskCpfCnpj,
  maskPhone,
} from "../../../../utils/masks";
import TipoClienteFilter from "../../../../components/tipoCliente/TipoClienteFilter";
import ErpFilter from "../../../../components/erp/ErpFilter";
import { getErpPrincipal, temErp } from "../../../../services/erpSistema";

// Tempo de espera antes de a busca livre virar requisição. Curto o bastante
// para parecer imediato, longo o bastante para uma palavra digitada inteira
// render uma consulta só.
const ESPERA_BUSCA_MS = 400;

const LINHAS_POR_PAGINA_PADRAO = 100;

// Com importação rodando, a tela consulta de perto para a barra andar. Parada,
// sonda de longe em longe — só para notar uma importação que outro usuário
// começou, já que a fila é global.
const POLL_IMPORTANDO_MS = 2000;
const POLL_OCIOSO_MS = 15000;

function erpConfig(erp) {
  let erpObj = {
    isGrupoClient: false,
    columnsErpType: "",
    temTipoCliente: false,
  };

  switch (erp) {
    case "HUBSOFT":
      return (erpObj = {
        isGrupoClient: true,
        isGrupoServico: true,
        columnsErpType: erp.toLowerCase(),
        labelGrupoCliente: "Grupo de cliente",
        temTipoCliente: true,
      });
      break;

    case "VOALLE":
      return (erpObj = {
        isGrupoClient: false,
        isGrupoServico: false,
        columnsErpType: erp.toLowerCase(),
        labelErp: "Contrato",
        labelGrupoCliente: "",
      });
      break;

    case "IXC":
      return (erpObj = {
        isGrupoClient: true,
        isGrupoServico: false,
        columnsErpType: erp.toLowerCase(),
        labelErp: "Contrato",
        labelGrupoCliente: "Tipo de cliente",
        temTipoCliente: true,
      });
    case "VERO":
      erpObj = {
        isGrupoClient: false,
        isGrupoServico: false,
        columnsErpType: erp.toLowerCase(),
        labelErp: "Contrato",
        labelGrupoCliente: "",
      };
      break;
  }
  return erpObj;
}

function maskCelular(celular) {
  if (celular.length < 11) {
    return maskCellPhone(celular);
  } else {
    return maskPhone(celular);
  }
}

// Formatadores fixos, criados uma vez só — trocar a função a cada render
// invalidaria as colunas da tabela sem necessidade.
const formatarCpfCnpj = criarFormatadorMascara(maskCpfCnpj);
const formatarCnpj = criarFormatadorMascara(maskCnpj);
const formatarTelefone = criarFormatadorMascara(maskCelular);

export default function IntergracaoErp() {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [openFilter, setOpenFilter] = useState(false);

  // `buscarCliente` é o que está no campo; `buscaAplicada` é o que já virou
  // consulta. A distância entre os dois é o debounce.
  const [buscarCliente, setBuscarCliente] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [grupoServicoSelecionado, setGrupoServicoSelecionado] = useState(null);
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  // ERPs cadastrados (opções do filtro) e o id do escolhido.
  const [erps, setErps] = useState([]);
  const [erpSelecionado, setErpSelecionado] = useState(null);

  // Colunas e filtros pelo tipo do ERP escolhido no filtro; sem escolha, o do
  // sistema (HubSoft cadastrado = padrão HubSoft).
  const erp =
    erps.find((item) => item.id === erpSelecionado)?.tipo ||
    (temErp("HUBSOFT") ? "HUBSOFT" : getErpPrincipal());

  const {
    isGrupoClient,
    isGrupoServico,
    columnsErpType,
    labelGrupoCliente,
    temTipoCliente,
  } = erpConfig(erp);

  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(
    LINHAS_POR_PAGINA_PADRAO,
  );
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: "asc" });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importando, setImportando] = useState(false);

  const [grupos, setGrupos] = useState([]);
  const [gruposServicos, setGruposServicos] = useState([]);

  // Seleção pelo id da linha de clientesErp (o código sozinho se repete entre
  // ERPs). As linhas das outras páginas não estão em
  // memória, então guardar a linha inteira não funcionaria.
  const [selecionados, setSelecionados] = useState(() => new Set());
  // "Todos os resultados do filtro", que pode ser muito maior que a seleção
  // explícita. Neste modo o servidor recebe o filtro, não a lista de códigos.
  const [selecaoTotal, setSelecaoTotal] = useState(false);

  // Estado do job de importação, que é global: pode ter sido iniciado por
  // outro usuário, em outra sessão.
  const [importacao, setImportacao] = useState({
    emExecucao: false,
    estado: null,
  });
  // O desfecho fica alguns minutos disponível no servidor, para a tela que
  // acompanhava conseguir lê-lo depois que a fila é liberada. Só que isso faria
  // uma tela recém-aberta anunciar "importação concluída" por algo que
  // aconteceu antes de ela existir — daí só mostrar para quem viu a transição.
  const [mostrarDesfecho, setMostrarDesfecho] = useState(false);

  const [snackbar, setSnackbar] = useState(null);

  const { setControllerDashboard, api } = useOutletContext();

  // Cancela a busca anterior quando outra começa: digitar rápido dispara várias
  // e só a última interessa.
  const controllerRef = useRef(null);

  const columnsErp = useMemo(() => {
    // Larguras em `flex` (proporção) com `minWidth`, para as colunas dividirem
    // a largura da tela sem nascer barra de rolagem horizontal à toa.

    // Uma definição só, compartilhada pelos ERPs que classificam PF/PJ, para
    // que o selo não divirja entre as tabelas.
    const colunaTipoCliente = {
      field: "tipoCliente",
      headerName: "Tipo",
      width: 60,
      renderCell: renderTipoCliente,
    };

    // ERP de origem: com mais de um ERP cadastrado, a mesma tabela traz
    // clientes de ERPs diferentes.
    const colunaErp = {
      field: "erp",
      headerName: "ERP",
      width: 90,
      sortable: false,
      renderCell: renderErp,
    };

    const colunaDocumento = {
      field: "cnpj",
      headerName: "CNPJ / CPF",
      flex: 1,
      minWidth: 120,
      valueFormatter: formatarCpfCnpj,
      renderCell: renderTexto,
    };

    const colunaCnpj = {
      field: "cnpj",
      headerName: "CNPJ",
      flex: 1,
      minWidth: 120,
      valueFormatter: formatarCnpj,
      renderCell: renderTexto,
    };

    const colunaCodigoCliente = {
      field: "codigoClient",
      headerName: "Código do Cliente",
      flex: 0.8,
      minWidth: 90,
      renderCell: renderTexto,
    };

    const colunaRazaoSocial = {
      field: "razaoSocial",
      headerName: "Razão Social",
      flex: 1.3,
      minWidth: 140,
      renderCell: renderTexto,
    };

    const colunaNomeFantasia = {
      field: "nomeFantasia",
      headerName: "Nome Fantasia",
      flex: 1.3,
      minWidth: 140,
      renderCell: renderTexto,
    };

    const colunaEmail = {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 130,
      renderCell: renderTexto,
    };

    const colunaGrupo = (field, headerName) => ({
      field,
      headerName,
      flex: 1.1,
      minWidth: 110,
      renderCell: renderChips,
    });

    const columnsHubsoft = [
      colunaCodigoCliente,
      colunaErp,
      colunaTipoCliente,
      colunaDocumento,
      colunaRazaoSocial,
      colunaNomeFantasia,
      colunaGrupo("grupoClient", "Grupo do Cliente"),
      colunaGrupo("grupoServico", "Grupo de Serviço"),
      colunaEmail,
    ];

    const columnsIxc = [
      colunaCodigoCliente,
      colunaErp,
      colunaTipoCliente,
      colunaDocumento,
      colunaRazaoSocial,
      colunaNomeFantasia,
      colunaGrupo("grupoClient", labelGrupoCliente),
      colunaEmail,
    ];

    const columnsVoalle = [colunaCnpj, colunaRazaoSocial, colunaNomeFantasia];

    const columnsVero = [
      colunaCodigoCliente,
      colunaCnpj,
      colunaRazaoSocial,
      colunaNomeFantasia,
      {
        field: "telefone",
        headerName: "Telefone",
        flex: 0.9,
        minWidth: 110,
        valueFormatter: formatarTelefone,
        renderCell: renderTexto,
      },
      colunaEmail,
    ];

    return {
      hubsoft: columnsHubsoft,
      voalle: columnsVoalle,
      ixc: columnsIxc,
      vero: columnsVero,
    };
  }, [labelGrupoCliente]);

  const colunasTabela = useMemo(
    () => (columnsErpType ? columnsErp[columnsErpType] || [] : []),
    [columnsErp, columnsErpType],
  );

  // O filtro corrente, num objeto só. É exatamente o que vai na listagem e o
  // que vai no "importar todos os resultados" — usar a mesma fonte é o que
  // garante que o botão importe o conjunto que está na tela.
  const filtrosAtuais = useMemo(
    () => ({
      busca: buscaAplicada || undefined,
      grupoClient: grupoSelecionado?.descricao || undefined,
      grupoServico: grupoServicoSelecionado?.display || undefined,
      // 0 é PJ, não "sem filtro" — por isso a checagem contra null.
      tipoCliente: tipoSelecionado === null ? undefined : tipoSelecionado,
      erpId: erpSelecionado || undefined,
    }),
    [
      buscaAplicada,
      grupoSelecionado,
      grupoServicoSelecionado,
      tipoSelecionado,
      erpSelecionado,
    ],
  );

  // Trocar de filtro volta para a primeira página e desliga o "todos os
  // resultados": ele se refere a um filtro, e o filtro mudou.
  const aoTrocarFiltro = useCallback((aplicar) => {
    aplicar();
    setPagina(0);
    setSelecaoTotal(false);
  }, []);

  useEffect(() => {
    const temporizador = setTimeout(() => {
      setBuscaAplicada((atual) => {
        const novo = buscarCliente.trim();
        if (novo !== atual) {
          setPagina(0);
          setSelecaoTotal(false);
        }
        return novo;
      });
    }, ESPERA_BUSCA_MS);

    return () => clearTimeout(temporizador);
  }, [buscarCliente]);

  // Opções dos seletores: vêm do ERP e não mudam enquanto a tela está aberta,
  // então são lidas uma vez por ERP escolhido. É por isso que têm rota própria
  // — se viessem com a listagem, o ERP seria consultado a cada tecla digitada.
  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        const resposta = await api.post("/find/clientesErp/filtros", {
          erpId: erpSelecionado || undefined,
        });
        if (cancelado) return;
        setGrupos(resposta?.data?.allGrupoClients || []);
        setGruposServicos(resposta?.data?.allGrupoServico || []);
        setErps(resposta?.data?.erps || []);
      } catch (error) {
        if (cancelado) return;
        console.error(error);
        setSnackbar({
          children: "Erro ao buscar os filtros do ERP",
          severity: "error",
        });
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [api, erpSelecionado]);

  const buscarPagina = useCallback(async () => {
    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;
    setControllerDashboard?.(controller);
    setLoading(true);

    try {
      const resposta = await api.post(
        "/find/clientesErp",
        {
          ...filtrosAtuais,
          pagina,
          tamanho: linhasPorPagina,
          ordenarPor: ordenacao.coluna || undefined,
          direcao: ordenacao.coluna ? ordenacao.direcao : undefined,
        },
        { signal: controller.signal },
      );

      setRows(resposta?.data?.clientes || []);
      setTotal(resposta?.data?.total || 0);
    } catch (error) {
      // Requisição cancelada por outra mais nova não é erro para o usuário.
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return;
      }
      console.error(error);
      setSnackbar({
        children: "Erro ao buscar clientes no ERP",
        severity: "error",
      });
    } finally {
      if (controllerRef.current === controller) setLoading(false);
    }
  }, [
    api,
    filtrosAtuais,
    pagina,
    linhasPorPagina,
    ordenacao,
    setControllerDashboard,
  ]);

  useEffect(() => {
    buscarPagina();
  }, [buscarPagina]);

  // Lidas por ref para o laço de polling não precisar reiniciar a cada troca de
  // filtro — ele tem cadência própria e não deve depender do estado da busca.
  const buscarPaginaRef = useRef(buscarPagina);
  const estavaEmExecucaoRef = useRef(false);
  buscarPaginaRef.current = buscarPagina;

  const consultarImportacao = useCallback(async () => {
    try {
      const resposta = await api.post("/find/clientesErp/importacao");
      const emExecucao = !!resposta?.data?.emExecucao;
      const estado = resposta?.data?.estado || null;

      setImportacao({ emExecucao, estado });

      // Transição de "rodando" para "parado": é aqui que a tela descobre o
      // desfecho e recarrega, porque os importados saíram da tabela.
      if (estavaEmExecucaoRef.current && !emExecucao) {
        if (estado?.status === "FALHA") {
          setSnackbar({
            children: "Importação interrompida — veja o aviso na tela",
            severity: "error",
          });
        } else if (estado?.status === "CONCLUIDO") {
          setSnackbar({
            children: `${Number(estado.importados || 0).toLocaleString("pt-BR")} cliente(s) adicionados`,
            severity: "success",
          });
        }
        setMostrarDesfecho(true);
        setSelecionados(new Set());
        setSelecaoTotal(false);
        setPagina(0);
        await buscarPaginaRef.current();
      }

      estavaEmExecucaoRef.current = emExecucao;
      return emExecucao;
    } catch (error) {
      // Falha de rede aqui não merece alarme: a próxima sondagem tenta de novo.
      console.error(error);
      return false;
    }
  }, [api]);

  useEffect(() => {
    let ativo = true;
    let temporizador = null;

    const ciclo = async () => {
      const rodando = await consultarImportacao();
      if (!ativo) return;
      temporizador = setTimeout(
        ciclo,
        rodando ? POLL_IMPORTANDO_MS : POLL_OCIOSO_MS,
      );
    };

    ciclo();

    return () => {
      ativo = false;
      clearTimeout(temporizador);
    };
  }, [consultarImportacao]);

  const paginaInteiraSelecionada =
    rows.length > 0 &&
    rows.every((linha) => selecionados.has(String(linha?.id)));

  // Quantos serão importados: no modo "todos os resultados" é o total do
  // filtro, que pode ser muito maior que a página.
  const quantidadeSelecionada = selecaoTotal ? total : selecionados.size;

  // Qualquer mexida manual na seleção desliga o "todos os resultados".
  const aoMudarSelecao = useCallback((novo) => {
    setSelecionados(novo);
    setSelecaoTotal(false);
  }, []);

  const limparSelecao = useCallback(() => {
    setSelecionados(new Set());
    setSelecaoTotal(false);
  }, []);

  const importarClientes = async () => {
    if (quantidadeSelecionada < 1 || importando || importacao.emExecucao)
      return;

    setImportando(true);
    try {
      const corpo = selecaoTotal
        ? // Manda o filtro, não os códigos: são até centenas de milhares, e o
          // servidor resolve a lista com o mesmo `where` da listagem.
          { todosDoFiltro: true, filtros: filtrosAtuais }
        : // Ids das linhas (código + ERP), não os códigos.
          { idsClientesErp: [...selecionados] };

      // A rota só enfileira; quem importa é um processo do servidor. O
      // acompanhamento vem do polling, não desta resposta.
      const resposta = await api.post("/create/clientesErp", corpo);

      setImportacao({
        emExecucao: true,
        estado: resposta?.data?.estado || null,
      });
      estavaEmExecucaoRef.current = true;
      setMostrarDesfecho(false);
      limparSelecao();

      setSnackbar({
        children: "Importação iniciada — acompanhe o progresso na tela",
        severity: "info",
      });
    } catch (error) {
      // 409: outra importação já está rodando. Não é erro da ação do usuário —
      // é a fila fazendo o que deve, então a tela passa a acompanhar aquela.
      if (error?.response?.status === 409) {
        setImportacao({
          emExecucao: true,
          estado: error.response.data?.estado || null,
        });
        estavaEmExecucaoRef.current = true;
        setSnackbar({
          children: "Já existe uma importação em andamento",
          severity: "warning",
        });
      } else {
        console.error(error);
        setSnackbar({
          children: "Erro ao iniciar a importação",
          severity: "error",
        });
      }
    } finally {
      setImportando(false);
    }
  };

  const actions = [
    {
      icon: <FilterAltOutlinedIcon />,
      name: "Filtro",
      onClick: () => setOpenFilter(true),
    },
  ];

  return (
    <>
      <FilterBar
        actions={actions}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        type={1}
        selectorComponentLeft={
          <Box
            sx={{
              display: "flex",
              gap: 1,
              "@media (max-width:1349px)": {
                flexDirection: "column",
              },
              width: "100%",
            }}
          >
            <TextField
              variant="outlined"
              label="Buscar cliente"
              value={buscarCliente}
              size="small"
              onChange={(event) => {
                setBuscarCliente(event.target.value);
              }}
              sx={{
                width: "100%",
                maxWidth: 500,
                fontSize: "0.75rem", // diminui fonte geral
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: buscarCliente ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setBuscarCliente("")} edge="end">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            {isGrupoClient && (
              <Autocomplete
                size="small"
                disablePortal
                options={grupos}
                getOptionLabel={(option) => option.descricao || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id_grupo_cliente === value?.id_grupo_cliente
                }
                onChange={(event, newValue) => {
                  aoTrocarFiltro(() => setGrupoSelecionado(newValue));
                }}
                value={grupoSelecionado}
                sx={{
                  width: "100%",
                  maxWidth: 500,
                }}
                renderInput={(params) => (
                  <TextField {...params} label={labelGrupoCliente} />
                )}
              />
            )}
            {erps.length > 0 && (
              <ErpFilter
                erps={erps}
                value={erpSelecionado}
                onChange={(valor) =>
                  aoTrocarFiltro(() => {
                    setErpSelecionado(valor);
                    // Grupos de cliente/serviço são do ERP anterior.
                    setGrupoSelecionado(null);
                    setGrupoServicoSelecionado(null);
                  })
                }
                id="filtro-erp-clientes-erp"
                sx={{
                  width: "100%",
                  maxWidth: 500,
                }}
              />
            )}
            {temTipoCliente && (
              <TipoClienteFilter
                value={tipoSelecionado}
                onChange={(valor) =>
                  aoTrocarFiltro(() => setTipoSelecionado(valor))
                }
                id="filtro-tipo-cliente-erp"
                sx={{
                  width: "100%",
                  maxWidth: 500,
                }}
              />
            )}
            {isGrupoServico && (
              <Autocomplete
                size="small"
                disablePortal
                options={gruposServicos}
                getOptionLabel={(option) => option.display || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                onChange={(event, newValue) => {
                  aoTrocarFiltro(() => setGrupoServicoSelecionado(newValue));
                }}
                value={grupoServicoSelecionado}
                sx={{
                  width: "100%",
                  maxWidth: 500,
                  fontSize: "0.75rem", // diminui fonte geral
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Grupo de servico" />
                )}
              />
            )}
          </Box>
        }
        selectorComponentRight={
          <Tooltip
            title={
              importacao.emExecucao
                ? "Há uma importação em andamento. Aguarde o término para iniciar outra."
                : ""
            }
          >
            {/* O span existe porque o Tooltip precisa de um filho que receba
                eventos, e um botão desabilitado não recebe. */}
            <span>
              <Button
                onClick={importarClientes}
                disabled={
                  quantidadeSelecionada < 1 ||
                  importando ||
                  importacao.emExecucao
                }
                variant="contained"
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: "max-content",
                }}
              >
                {importacao.emExecucao
                  ? "Importação em andamento"
                  : importando
                    ? "Iniciando..."
                    : quantidadeSelecionada > 0
                      ? `Adicionar ${quantidadeSelecionada.toLocaleString("pt-BR")} cliente${
                          quantidadeSelecionada > 1 ? "s" : ""
                        }`
                      : "Adicionar cliente"}
              </Button>
            </span>
          </Tooltip>
        }
      />

      <Paper sx={styles.containerPaper}>
        <Typography variant="h6" sx={styles.titleDataGrid}>
          Clientes do ERP
        </Typography>

        {/* Acompanhamento do job. Aparece para qualquer usuário com a tela
            aberta, porque a fila é global — é o que explica o botão travado
            para quem não iniciou a importação. */}
        {(importacao.emExecucao ||
          (mostrarDesfecho && importacao.estado?.status)) && (
          <ProgressoImportacao
            estado={importacao.estado}
            sx={styles.avisoImportacao}
          />
        )}

        {/* Ponte entre "selecionei esta página" e "quero tudo que o filtro
            devolve" — sem ela não haveria como pedir mais do que cabe numa
            página. */}
        {(selecaoTotal ||
          (paginaInteiraSelecionada && total > rows.length)) && (
          <Box sx={styles.avisoSelecao}>
            {selecaoTotal ? (
              <Typography variant="body2">
                Todos os <strong>{total.toLocaleString("pt-BR")}</strong>{" "}
                resultados deste filtro estão selecionados.{" "}
                <Link component="button" type="button" onClick={limparSelecao}>
                  Limpar seleção
                </Link>
              </Typography>
            ) : (
              <Typography variant="body2">
                Os <strong>{rows.length}</strong> desta página estão
                selecionados.{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => setSelecaoTotal(true)}
                >
                  Selecionar todos os {total.toLocaleString("pt-BR")} resultados
                </Link>
              </Typography>
            )}
          </Box>
        )}

        <Box sx={styles.boxDatagrid}>
          <TabelaClientesErp
            rows={rows}
            columns={colunasTabela}
            loading={loading}
            total={total}
            pagina={pagina}
            onPaginaChange={setPagina}
            linhasPorPagina={linhasPorPagina}
            onLinhasPorPaginaChange={(valor) =>
              aoTrocarFiltro(() => setLinhasPorPagina(valor))
            }
            ordenacao={ordenacao}
            onOrdenacaoChange={(nova) => {
              setOrdenacao(nova);
              setPagina(0);
            }}
            selecionados={selecionados}
            onSelecionadosChange={aoMudarSelecao}
            selecaoTotal={selecaoTotal}
            height="68vh"
          />
        </Box>
      </Paper>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
